import type { Channel } from 'phoenix';
import { useEffect, useRef } from 'react';
import { useEffectEvent } from 'use-effect-event';
import useAppSelector from '../app/redux/useAppSelector';
import useSlug from '../utils/router/useSlug';

let shouldDebugAiCopilot = localStorage.getItem('aiCopilotDebug') === 'true';

if (typeof window !== 'undefined' && window.__slab.DEVTOOLS) {
  window.__slab.DEVTOOLS.setDebugAiCopilot = (enabled: boolean) => {
    localStorage.setItem('aiCopilotDebug', enabled ? 'true' : 'false');
    shouldDebugAiCopilot = enabled;
  };
}

const consoleInfo = (...args: unknown[]) => {
  if (shouldDebugAiCopilot) {
    // eslint-disable-next-line no-console
    console.info(...args);
  }
};

export type Source = {
  type: 'post';
  id: string;
  title: string;
};

export type InputSources = {
  slab: {
    enabled: boolean;
  };
  web: {
    enabled: boolean;
  };
  mentionedPosts: {
    enabled: boolean;
    postIds: string[];
  };
  currentPost: {
    enabled: boolean;
  };
};

interface UseAiCopilotOptions {
  channelId?: string;
  onProgress: (
    responseId: string,
    status: 'start' | 'search' | 'reason',
  ) => void;
  onAnswerText: (responseId: string, text: string) => void;
  onFinish: (responseId: string, sources: Source[], text: string) => void;
  onError: (error: string) => void;
}

const useAiCopilot = (options: UseAiCopilotOptions) => {
  const { channelId, onProgress, onAnswerText, onFinish, onError } = options;

  const [currentPostId] = useSlug('postSlug');
  const onProgressEvent = useEffectEvent(onProgress);
  const onAnswerTextEvent = useEffectEvent(onAnswerText);
  const onFinishEvent = useEffectEvent(onFinish);
  const onErrorEvent = useEffectEvent(onError);

  const channelRef = useRef<Channel | null>(null);

  const socket = useAppSelector((state) => state.session.socket);

  useEffect(() => {
    if (!socket || !channelId) return undefined;
    const channel = socket.channel(`stream:ai_copilot_${channelId}`);

    channel.onMessage = (event, payload) => {
      consoleInfo(event, payload);
      return payload;
    };

    channel.on('progress', ({ response_id: responseId, status }) => {
      if (status === 'start' || status === 'search' || status === 'reason') {
        onProgressEvent(responseId, status);
      }
    });

    channel.on('update', ({ response_id: responseId, text }) => {
      onAnswerTextEvent(responseId, text);
    });

    channel.on('complete', ({ response_id: responseId, sources, text }) => {
      consoleInfo('complete', responseId, sources, text);
      onFinishEvent(
        responseId,
        sources.filter((source: { type: string }) => source.type === 'post'),
        text,
      );
    });
    channel.on('error', () => {
      onErrorEvent('Sorry, I ran into an error. Please try again.');
    });

    channel.join();
    consoleInfo('joined');

    channelRef.current = channel;
    return () => {
      channel.leave();
      channelRef.current = null;
    };
  }, [socket, channelId]);

  const sendMessage = ({
    previousId,
    input,
    inputSources,
    retry,
  }: {
    previousId: string | null;
    input: string;
    inputSources: InputSources;
    retry?: boolean;
  }): boolean => {
    const channel = channelRef.current;
    if (!channel) return false;

    const postIds = (() => {
      if (inputSources.mentionedPosts.enabled) {
        return inputSources.mentionedPosts.postIds;
      }
      if (currentPostId && inputSources.currentPost.enabled) {
        return [currentPostId];
      }
      return [];
    })();

    const params = {
      input: input.trim(),
      previous_id: previousId,
      post_ids: postIds,
      tools: [
        ...(inputSources.slab.enabled ? ['slab'] : []),
        ...(inputSources.web.enabled ? ['web'] : []),
      ],
      retry,
    };

    consoleInfo('sendMessage', params);
    channel.push('copilot', params).receive('error', () => {
      onErrorEvent('Sorry, I ran into an error. Please try again.');
    });

    return true;
  };

  return {
    sendMessage,
  };
};

export default useAiCopilot;
